import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import { AppLayout } from '@workspace/seedforge-ui/components/app-layout'
import { UserMenu } from '@workspace/seedforge-ui/components/user-menu'
import { LayoutDashboard } from 'lucide-react'
import { AdminGuard } from '../features/admin/components/admin-guard'
import { buildAdminNavSections } from '../features/admin/components/admin-nav'
import { useAdminPendingCounts } from '../features/admin/hooks/use-admin-pending-counts'
import { useCurrentUserQuery } from '../hooks/api'
import { useSignOut } from '../hooks/use-sign-out'
import { useSession } from '../lib/auth'

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
})

function getInitials(name?: string, email?: string): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

function AdminLayout() {
  const { data: session } = useSession()
  const { data: user } = useCurrentUserQuery({ enabled: !!session })
  const signOut = useSignOut()
  const location = useLocation()
  const pendingCounts = useAdminPendingCounts()
  const navSections = buildAdminNavSections(pendingCounts)

  // Derive activeNav from current pathname. Pick the longest matching `to`
  // so nested routes (e.g. /admin/investors/persona-research) don't get
  // swallowed by their parent (/admin/investors).
  const path = location.pathname
  const activeNav =
    navSections
      .flatMap((s) => s.items)
      .map((item) => ({ label: item.label, to: item.to ?? '' }))
      .filter((item) => item.to !== '' && (path === item.to || path.startsWith(`${item.to}/`)))
      .sort((a, b) => b.to.length - a.to.length)[0]?.label ?? ''

  const userName = user?.name ?? session?.user?.name ?? 'Admin'
  const userEmail = user?.email ?? session?.user?.email ?? ''

  return (
    <AdminGuard>
      <AppLayout
        activeNav={activeNav}
        navSections={navSections}
        logoSrc="/seedforge-icon.svg"
        topbarRight={
          <UserMenu
            initials={getInitials(userName, userEmail)}
            name={userName}
            email={userEmail}
            extraLinks={[{ icon: LayoutDashboard, label: 'Back to App', to: '/app' }]}
            onSignOut={signOut}
          />
        }
      >
        <Outlet />
      </AppLayout>
    </AdminGuard>
  )
}
